// go-pim-ui API client.
//
// One axios instance, talks to the go-pim admin API only. The Authorization
// header is injected from the auth store.
//
// Tenant scoping: go-pim is COMPANY-scoped, and v1 passes company_id
// EXPLICITLY IN THE PATH (GET /v1/companies/:id/products, …) — no tenant
// header. strata's X-Merchant-ID injection + the 403-evicts-stuck-tenant
// interceptor branch were deleted here. (If a header-based CompanySwitcher is
// wanted later, re-introduce an X-Company-ID injection cribbed from the deleted
// merchant path.)
//
// On 401 the response interceptor calls authStore.refresh() once and retries
// the original request. If refresh fails, the request rejects with the
// original 401 and the RequireAuth guard handles the redirect.
//
// JSON codec (money is bigint end-to-end): transformRequest + transformResponse
// run through json-bigint so bigint money fields survive the wire in both
// directions. Default JSON.stringify throws on bigint; default JSON.parse
// silently rounds integers past 2^53. See @/utils/bigjson for the rationale.
import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

import { useAuth } from "@/store/auth";
import { parseJsonBig, stringifyJsonBig } from "@/utils/bigjson";
import { queryClient } from "@/queryClient";

const baseURL = import.meta.env.VITE_PIM_API_URL || "";

// Skip transformer-side JSON-ification for body types axios's default
// (or the browser) serializes itself. Matches axios's built-in skip list.
function isBinaryBody(data: unknown): boolean {
  if (typeof FormData !== "undefined" && data instanceof FormData) return true;
  if (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams)
    return true;
  if (typeof Blob !== "undefined" && data instanceof Blob) return true;
  if (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer)
    return true;
  return false;
}

function transformReqJsonBig(data: unknown, headers: AxiosHeaders): unknown {
  if (data == null) return data;
  if (typeof data === "string") return data;
  if (isBinaryBody(data)) return data;
  // Object / array body — needs bigint-safe JSON. json-bigint emits bigint as
  // a plain JSON integer (no "n" suffix); JSON.stringify would throw outright.
  headers.set("Content-Type", "application/json");
  return stringifyJsonBig(data);
}

// MalformedJsonResponseError is thrown by transformResJsonBig when a
// nominally-successful (2xx) response body is not parseable JSON — typically
// when a misconfigured reverse proxy or CDN intercepts the request and serves
// an HTML error page with status 200. Throwing here turns it into a loud axios
// error that flows through apiErrorMessage and surfaces the transport issue.
export class MalformedJsonResponseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MalformedJsonResponseError";
  }
}

// Exported for unit tests only. Production code wires this in via
// `transformResponse: [transformResJsonBig]` on the apiClient below.
export function transformResJsonBig(
  data: unknown,
  headers?: unknown,
  status?: number,
): unknown {
  if (typeof data !== "string" || data === "") return data;
  try {
    return parseJsonBig(data);
  } catch {
    // Parse failed. Two paths:
    //  1. Non-2xx: pass the body through unchanged so apiErrorMessage's
    //     status-based fallback can render something.
    //  2. 2xx with non-JSON body: a successful HTTP status but a payload
    //     nobody downstream can handle. Throw a typed error so the failure is
    //     loud and routable rather than a confusing deep TypeError.
    const ok = status != null && status >= 200 && status < 300;
    if (ok) {
      let ct = "";
      if (headers && typeof headers === "object") {
        const h = headers as Record<string, unknown>;
        const raw = h["content-type"] ?? h["Content-Type"];
        if (typeof raw === "string") ct = raw;
      }
      const prefix = data.slice(0, 40).replace(/\s+/g, " ").trim();
      throw new MalformedJsonResponseError(
        `Expected JSON response (status ${status}, content-type "${ct}") ` +
          `but got: ${prefix}${data.length > 40 ? "…" : ""}`,
      );
    }
    return data;
  }
}

export const apiClient = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15_000,
  transformRequest: [transformReqJsonBig],
  transformResponse: [transformResJsonBig],
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuth.getState();
  if (accessToken) {
    config.headers = config.headers ?? {};
    config.headers["Authorization"] = `Bearer ${accessToken}`;
  }
  return config;
});

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // 403 against any scoped endpoint may signal that the user's role was
    // downgraded server-side. Force-invalidate ['me'] so RequireAuth
    // re-evaluates against the fresh role on the next render rather than
    // waiting up to 5 minutes for the next useMe poll. Cheap: a single /v1/me
    // fetch, dedup'd by the queryClient if multiple 403s land at once.
    if (status === 403) {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    }

    // Don't try to refresh on the refresh endpoint itself, or if we already retried.
    const isRefreshCall = original?.url?.endsWith("/v1/auth/refresh");

    if (status === 401 && original && !original._retried && !isRefreshCall) {
      original._retried = true;
      const ok = await useAuth.getState().refresh();
      if (ok) {
        // Re-stamp the new bearer. The frozen `original` config still carries
        // the EXPIRED token; axios.request() does not re-fire the request
        // interceptor for an already-populated InternalAxiosRequestConfig, so
        // without this overwrite the retry sends the stale Authorization,
        // gets another 401, and the user appears logged out.
        const { accessToken } = useAuth.getState();
        if (accessToken) {
          original.headers = {
            ...(original.headers ?? {}),
            Authorization: `Bearer ${accessToken}`,
          };
        }
        return apiClient.request(original);
      }
    }
    return Promise.reject(error);
  },
);
